import React from 'react'
import Button from '@mui/material/Button'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MuiAlert from '@mui/lab/Alert'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import jwt from 'jsonwebtoken'
import { getCookie } from '../../utils/cookie'

interface IState {
  isAboutToExpire: boolean
  isExpired: boolean
  idleWarning: boolean
  expiredByIdle: boolean
  lastLoginNotice: string | null
}

const showFirstNotificationBeforeExpireMillSeconds = 1000 * 60 * 5

const IDLE_TIMEOUT_MS = 15 * 60 * 1000
const IDLE_WARN_MS = 13 * 60 * 1000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']

class SessionExpireNotifier extends React.Component<unknown, IState> {
  private idleWarnTimer?: ReturnType<typeof setTimeout>

  private idleLogoutTimer?: ReturnType<typeof setTimeout>

  private expiryWarnTimer?: ReturnType<typeof setTimeout>

  private expiryLogoutTimer?: ReturnType<typeof setTimeout>

  private activityHandler?: () => void

  constructor(props) {
    super(props)

    this.state = {
      isAboutToExpire: false,
      isExpired: false,
      idleWarning: false,
      expiredByIdle: false,
      lastLoginNotice: null,
    }
  }

  componentDidMount() {
    const cookie = getCookie('zykrr')
    if (!cookie) return
    const parsedJWT = jwt.decode(cookie) as { [key: string]: any }
    if (!parsedJWT) return

    this.maybeShowLastLoginNotice(parsedJWT)

    const tokenAliveTime = (new Date(parsedJWT.exp * 1000).getTime() - new Date().getTime())
    if (tokenAliveTime <= 0) {
      this.setState({ isAboutToExpire: false, isExpired: true })
      this.handleRelogin()
    } else {
      this.expiryWarnTimer = setTimeout(() => {
        this.setState({ isAboutToExpire: true })

        this.expiryLogoutTimer = setTimeout(() => {
          this.setState({ isAboutToExpire: false, isExpired: true })
        }, showFirstNotificationBeforeExpireMillSeconds)
      }, tokenAliveTime - showFirstNotificationBeforeExpireMillSeconds)
    }

    this.activityHandler = this.resetIdleTimers
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, this.activityHandler as () => void, { passive: true }))
    this.resetIdleTimers()
  }

  componentWillUnmount() {
    this.clearIdleTimers()
    if (this.expiryWarnTimer) clearTimeout(this.expiryWarnTimer)
    if (this.expiryLogoutTimer) clearTimeout(this.expiryLogoutTimer)
    if (this.activityHandler) {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, this.activityHandler as () => void))
    }
  }

  clearIdleTimers = () => {
    if (this.idleWarnTimer) clearTimeout(this.idleWarnTimer)
    if (this.idleLogoutTimer) clearTimeout(this.idleLogoutTimer)
  }

  resetIdleTimers = () => {
    if (this.state.isExpired) return
    this.clearIdleTimers()
    if (this.state.isAboutToExpire && this.state.idleWarning) {
      this.setState({ isAboutToExpire: false, idleWarning: false })
    }

    this.idleWarnTimer = setTimeout(() => {
      this.setState({ isAboutToExpire: true, idleWarning: true })
    }, IDLE_WARN_MS)

    this.idleLogoutTimer = setTimeout(() => {
      this.setState({ isAboutToExpire: false, isExpired: true, expiredByIdle: true })
      this.handleRelogin()
    }, IDLE_TIMEOUT_MS)
  }

  maybeShowLastLoginNotice = (parsedJWT: { [key: string]: any }) => {
    const prev = parsedJWT.previousLoginTime
    if (!prev) return
    const seenKey = `lastLoginNoticeShown:${parsedJWT.iat}`
    try {
      if (sessionStorage.getItem(seenKey)) return
      sessionStorage.setItem(seenKey, '1')
    } catch (e) {
      // noop
    }
    this.setState({ lastLoginNotice: new Date(prev).toLocaleString() })
  }

  handleDismissLastLogin = () => {
    this.setState({ lastLoginNotice: null })
  }

  handleRelogin = () => {
    localStorage.clear()
    window.location.href = `${process.env.AUTH_HOST}/sign_out?continue=${encodeURI(
      window.location.href,
    )}`
  }

  handleStayLoggedIn = () => {
    this.resetIdleTimers()
  }

  renderWarningDialog(opts: {
    title: string; message: React.ReactNode; primaryLabel: string; onPrimary: () => void;
    secondaryLabel?: string; onSecondary?: () => void;
  }) {
    const { title, message, primaryLabel, onPrimary, secondaryLabel, onSecondary } = opts
    return (
      <Dialog open maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12, padding: 8 } }}>
        <DialogContent style={{ textAlign: 'center', paddingTop: 28 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              width: 56, height: 56, margin: '0 auto 16px', borderRadius: '50%',
              backgroundColor: '#FFF4E5',
            }}
          >
            <AccessTimeIcon style={{ fontSize: 30, color: '#ED6C02' }} />
          </Box>
          <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.6 }}>
            {message}
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: 20, gap: 12 }}>
          {secondaryLabel && (
            <Button onClick={onSecondary} color="inherit" variant="outlined" style={{ borderRadius: 8, textTransform: 'none', minWidth: 120 }}>
              {secondaryLabel}
            </Button>
          )}
          <Button onClick={onPrimary} color="primary" variant="contained" disableElevation style={{ borderRadius: 8, textTransform: 'none', minWidth: 120 }}>
            {primaryLabel}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  render() {
    if (this.state.isAboutToExpire && this.state.idleWarning) {
      return this.renderWarningDialog({
        title: 'Are you still there?',
        message: "You've been inactive for a while and will be signed out soon to keep your account secure.",
        primaryLabel: "I'm still here",
        onPrimary: this.handleStayLoggedIn,
        secondaryLabel: 'Sign out',
        onSecondary: this.handleRelogin,
      })
    }

    if (this.state.isAboutToExpire) {
      return this.renderWarningDialog({
        title: 'Session expiring soon',
        message: 'Your session is about to expire. Please save your work and sign in again to continue.',
        primaryLabel: 'Sign in again',
        onPrimary: this.handleRelogin,
      })
    }

    if (this.state.isExpired) {
      return (
        <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <MuiAlert
            severity="info"
            elevation={6}
            variant="filled"
            style={{ borderRadius: 8, alignItems: 'center' }}
            action={(
              <Button color="inherit" size="small" variant="outlined" onClick={this.handleRelogin}>
                Sign in
              </Button>
            )}
          >
            {this.state.expiredByIdle
              ? "You've been signed out due to inactivity."
              : 'Your session has expired.'}
          </MuiAlert>
        </Snackbar>
      )
    }

    if (this.state.lastLoginNotice) {
      return (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={12000}
          onClose={this.handleDismissLastLogin}
        >
          <MuiAlert
            severity="info"
            elevation={6}
            variant="filled"
            onClose={this.handleDismissLastLogin}
            style={{ borderRadius: 8, alignItems: 'center' }}
          >
            {`Last login: ${this.state.lastLoginNotice}. If this wasn't you, please reset your password.`}
          </MuiAlert>
        </Snackbar>
      )
    }

    return null
  }
}

export default SessionExpireNotifier
